import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  LoadingDialogComponent,
} from "@cotecna/dialogs";
import { ActionElement } from "@cotecna/material";
import {
  ColumnDataType,
  TableAction,
  TableActionResult,
  TableColumn,
  ToggleEvent,
} from "@cotecna/tables";
import { BehaviorSubject, firstValueFrom, Subscription } from "rxjs";
import { UserService } from "src/app/session/services/user.service";
import { Template } from "src/app/settings/models/template";
import { TemplatesService } from "src/app/settings/services/templates.service";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";

import { ItemsService } from "../../../items/services/items.service";
import {
  DOWNLOAD_DOCUMENT,
  FAB_ACTION_GENERATE_DOCUMENT,
  FAB_ACTION_UPLOAD_DOCUMENT,
} from "../../constants/constants";
import { AddDocumentDialogData } from "../../dialogs/add-document/add-document-dialog-data";
import { AddDocumentComponent } from "../../dialogs/add-document/add-document.component";
import { GenerateDocumentDialogData } from "../../dialogs/generate-document-dialog/generate-document-dialog-data";
import { GenerateDocumentDialogComponent } from "../../dialogs/generate-document-dialog/generate-document-dialog.component";
import { ShareEmailDialogComponent } from "../../dialogs/share-email-dialog/share-email-dialog.component";
import { ViewDocumentsDialogComponent } from "../../dialogs/view-documents-dialog/view-documents-dialog.component";
import { MultiSendByEmail } from "../../model/document-to-Share";
import {
  DocumentType,
  InspectionDocument,
} from "../../model/inspection-document";
import { ItemLight } from "../../model/itemLight";
import { InspectionDetailsService } from "../../services/inspection-details.service";
import { InspectionDocumentService } from "../../services/inspection-document-service/inspection-document.service";
import { PermissionsService } from "../../services/permissions.service";
import { ReportsService } from "./../../services/inspection-report/inspection-report.service";
import { DOCUMENTS_TABLE_COLUMNS } from "./documents-table-columns";

const downloadAction = {
  icon: "get_app",
  name: "download",
  tooltip: "Download",
} as TableAction;

const emailAction = {
  icon: "mail_outline",
  name: "email",
  tooltip: "Email",
} as TableAction;

const deleteAction = {
  icon: "clear",
  name: "delete",
  tooltip: "Delete",
} as TableAction;

const viewAction = {
  icon: "visibility",
  name: "view",
  tooltip: "View",
} as TableAction;

@Component({
  selector: "im-inspection-documents",
  templateUrl: "./inspection-documents.component.html",
  styleUrls: ["./inspection-documents.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InspectionDocumentsComponent implements OnInit, OnDestroy {
  public speedDialActions: ActionElement[] = [
    {
      icon: "description",
      name: FAB_ACTION_GENERATE_DOCUMENT,
      text: FAB_ACTION_GENERATE_DOCUMENT,
      tooltip: FAB_ACTION_GENERATE_DOCUMENT,
    },
    {
      icon: "upload",
      name: FAB_ACTION_UPLOAD_DOCUMENT,
      text: FAB_ACTION_UPLOAD_DOCUMENT,
      tooltip: FAB_ACTION_UPLOAD_DOCUMENT,
    },
  ];

  public documents = new BehaviorSubject<InspectionDocument[]>([]);
  public columns: TableColumn[];
  public actions: TableAction[];
  public isLoading: boolean = true;
  public addDocumentAllowed: boolean;
  private inspectionId: string;
  private templatesInfo: Template[] = [];
  public confidentialityClause =
    "Please be reminded that the documents attached are strictly confidential and should not be communicated (either directly or indirectly) to any third person.";
  public isCordinatorReadonly: boolean = false;
  public clearAllSelection = new BehaviorSubject<InspectionDocument[]>([]);
  private notifierSubscription: Subscription;
  private restartCheckedDocumentsSubscription: Subscription;

  public constructor(
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private documentService: InspectionDocumentService,
    private itemsService: ItemsService,
    private inspectionReportService: ReportsService,
    private permissionService: PermissionsService,
    private userRolePermissionsService: UserRolePermissionsService,
    private inspectionDetailService: InspectionDetailsService,
    private templatesService: TemplatesService,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.notifierSubscription =
      this.inspectionDetailService.eventEmitterNotifier?.subscribe(() => {
        this.getInspectionDocuments();
      });
  }

  public async ngOnInit() {
    this.notifierSubscription =
      this.inspectionDetailService.eventEmitterNotifier?.subscribe(() => {
        this.getInspectionDocuments();
      });
    this.getInspectionIdFromUrl();
    await this.setPermissions(this.permissionService);
    const user = await this.userService.getCurrentUser();
    this.getColumns(user.accountName);

    this.isCordinatorReadonly = this.userRolePermissionsService.isReadOnly();

    await Promise.all([
      this.getInspectionDocuments(),
      this.getTemplatesList(),
    ]).finally(() => {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
    });

    this.inspectionReportService.inspectionReportGenerated.subscribe(
      (result) => {
        if (result) this.getInspectionDocuments();
      },
    );

    this.restartCheckedDocumentsSubscription =
      this.documentService.restartCheckedDocumentsObservable?.subscribe(
        async (value) => {
          if (value) {
            this.clearAllSelection.next([]);
          }
        },
      );
  }

  public async openAddDocumentDialog(): Promise<void> {
    const dialogRef = this.dialog.open(AddDocumentComponent, {
      data: {
        inspectionId: this.inspectionId,
      } as AddDocumentDialogData,
      panelClass: "dialog",
    });
    const dialogResult = await dialogRef?.afterClosed().toPromise();
    if (dialogResult) {
      this.getInspectionDocuments();
    }
  }

  public async processAction(actionResult: TableActionResult) {
    const document = actionResult.data as InspectionDocument;
    switch (actionResult.action) {
      case emailAction: {
        let defaultEmails = [];
        let allowAdditionalEmails = true;
        const foundTemplate = this.templatesInfo.find(
          (template) => template.templateId == actionResult.data.templateId,
        );
        if (foundTemplate) {
          defaultEmails = foundTemplate.defaultEmails;
          allowAdditionalEmails = foundTemplate.allowAdditionalEmails;
        }
        const dialogRef = this.dialog.open(ShareEmailDialogComponent, {
          data: {
            defaultEmails: defaultEmails,
            allowAdditionalEmails: allowAdditionalEmails,
          },
          panelClass: "dialog",
          width: "50vw",
          height: "40vh",
          autoFocus: false,
        });
        const result = await dialogRef?.afterClosed().toPromise();
        if (result) {
          const multiSendByEmail: MultiSendByEmail = {
            inspectionId: this.inspectionId,
            documentsToSend: [
              {
                documentId: actionResult.data.documentId,
                documentName: actionResult.data.name,
              },
            ],
            destinationEmails: result.emailsToSend,
          };
          await this.documentService.sendByEmail(multiSendByEmail);
        }
        break;
      }
      case downloadAction: {
        const loadingDialogRef = this.openLoadingDialog(DOWNLOAD_DOCUMENT);
        try {
          await this.documentService.downloadDocument(
            this.inspectionId,
            document,
          );
        } finally {
          loadingDialogRef.close();
        }
        break;
      }
      case deleteAction: {
        const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
          data: <ConfirmDialogData>{
            icon: "delete_outline",
            okButton: {
              text: "delete",
              color: "warn",
            },
            title: "Delete Document",
            message: `Are you sure you want to delete "${document.name}" from the documents list?`,
          },
          panelClass: "share-document-dialog",
          autoFocus: false,
        });
        const confirmDialogResult = await confirmDialog
          .afterClosed()
          .toPromise();
        if (confirmDialogResult) {
          this.isLoading = true;
          await this.documentService.deleteDocument(
            this.inspectionId,
            document,
          );
          await this.getInspectionDocuments();
          this.isLoading = false;
        }
        break;
      }
      case viewAction: {
        this.openViewDocumentDialog(actionResult.data.documentId);
      }
    }
  }

  private async openViewDocumentDialog(
    selectedDocumentId: string,
  ): Promise<void> {
    let loadingDialog: MatDialogRef<any> = null;
    let viewDocumentDialog: MatDialogRef<any> = null;
    try {
      loadingDialog = this.dialog.open(LoadingDialogComponent, {
        autoFocus: false,
        disableClose: true,
        data: "Loading options",
      });
      loadingDialog?.close();
      viewDocumentDialog = this.dialog.open(ViewDocumentsDialogComponent, {
        data: {
          inspectionId: this.inspectionId,
          selectedDocument: selectedDocumentId,
          documents: this.documents.getValue(),
        },
        width: "70vw",
        height: "90vh",
        disableClose: true,
      });
      const result = await firstValueFrom(viewDocumentDialog?.afterClosed());
      if (result === "refresh") {
        await this.getInspectionDocuments();
      }
    } finally {
      loadingDialog?.close();
    }
  }

  public async onToogleChanged(toggleEvent: ToggleEvent) {
    if (toggleEvent.event.checked) {
      this.shareDocument(toggleEvent);
    } else {
      this.notShareDocument(toggleEvent);
    }
  }

  private async shareDocument(toggleEvent: ToggleEvent) {
    const confirmShareDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "share",
        okButton: {
          text: "confirm",
          color: "primary",
        },
        title: "Share document on external sources",
        message: `Confirm to share the Document: "${toggleEvent.row.name}" on external sources with client`,
      },
      panelClass: "share-document-dialog",
    });
    const result = await confirmShareDialog.afterClosed().toPromise();
    if (result) {
      await this.documentService.updateSharedDocument(
        this.inspectionId,
        toggleEvent.row.documentId,
      );
    } else {
      toggleEvent.event.source.checked = false;
    }
  }

  private async notShareDocument(toggleEvent: ToggleEvent) {
    const confirmNoShareDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "share",
        okButton: {
          text: "confirm",
          color: "warn",
        },
        title: "Stop sharing document on external sources",
        message: `Confirm to stop sharing the Document: "${toggleEvent.row.name}" on external sources with client`,
      },
      panelClass: "share-document-dialog",
    });
    const result = await confirmNoShareDialog.afterClosed().toPromise();
    if (result) {
      await this.documentService.updateSharedDocument(
        this.inspectionId,
        toggleEvent.row.documentId,
      );
    } else {
      toggleEvent.event.source.checked = true;
    }
  }

  public onFabSpeedDialSelectedAction(actionElement: ActionElement): void {
    const actionName = actionElement.name;
    switch (actionName) {
      case FAB_ACTION_UPLOAD_DOCUMENT:
        this.openAddDocumentDialog();
        break;
      case FAB_ACTION_GENERATE_DOCUMENT:
        this.openGenerateDocumentDialog();
        break;
    }
  }

  public async openGenerateDocumentDialog(): Promise<void> {
    let loadingDialog: MatDialogRef<any> = null;
    let generateDocumentDialog: MatDialogRef<any> = null;
    try {
      loadingDialog = this.dialog.open(LoadingDialogComponent, {
        autoFocus: false,
        disableClose: true,
        data: "Loading options",
      });
      const itemsInfo = await this.getInspectionItems();
      loadingDialog?.close();
      generateDocumentDialog = this.dialog.open(
        GenerateDocumentDialogComponent,
        {
          data: {
            items: itemsInfo,
            templates: this.templatesInfo,
          } as GenerateDocumentDialogData,
          panelClass: "dialog",
          width: "50vw",
          height: "60vh",
        },
      );
      const result = await generateDocumentDialog?.afterClosed().toPromise();
      if (result) {
        await this.generateReport(result);
      }
    } finally {
      loadingDialog?.close();
      generateDocumentDialog?.close();
    }
  }

  private getInspectionIdFromUrl(): void {
    this.inspectionId = this.activatedRoute.parent?.snapshot.paramMap.get("id");
  }

  private getColumns(accountName: string): void {
    this.columns = JSON.parse(JSON.stringify(DOCUMENTS_TABLE_COLUMNS));
    if (!this.userRolePermissionsService.isReadOnly()) {
      this.columns.find((column) => column.dataProperty == "name").width = 40;

      if (this.userRolePermissionsService.isDocumentShareAllowed(accountName)) {
        this.columns.push({
          dataProperty: "shared",
          isSorting: false,
          label: "Shared",
          dataType: ColumnDataType.Toggle,
          width: 80,
        });
      }
    }
  }

  private async getInspectionDocuments(): Promise<InspectionDocument[]> {
    const documents = this.inspectionId
      ? await this.documentService.getDocumentList(this.inspectionId)
      : [];
    documents.map((document) => {
      document.documentTypeName = this.getDocumentType(document.documentType);
    });
    if (this.userRolePermissionsService.isReadOnly()) {
      const filteredDocuments = documents.filter((document) => document.shared);
      this.documents.next(filteredDocuments);
    } else {
      this.documents.next(documents);
    }
    this.changeDetectorRef.markForCheck();
    return documents;
  }

  private async getTemplatesList(): Promise<Template[]> {
    this.templatesInfo = await this.templatesService.getTemplatesWithPreset(
      this.inspectionId,
    );
    if (this.templatesInfo.length == 0)
      this.templatesInfo = await this.templatesService.getTemplatesList();
    return this.templatesInfo;
  }

  private getDocumentType(documentType: DocumentType): string {
    switch (documentType) {
      case DocumentType.Report:
        return "Report";
      case DocumentType.SupportingDocument:
        return "Supporting Document";
      case DocumentType.Certificate:
        return "Certificate";
      case DocumentType.FieldReport:
        return "Field Report";
      case DocumentType.Invoice:
        return "Invoice";
      case DocumentType.BillOfLading:
        return "Bill Of Lading";
      case DocumentType.PackingList:
        return "Packing List";
      case DocumentType.RFC:
        return "RFC";
      case DocumentType.ValidTestReport:
        return "Valid Test Report";
      case DocumentType.LetterofCredit:
        return "Letter of Credit";
      case DocumentType.ProductImage:
        return "Product Image";
      case DocumentType.Others:
        return "Others";
      case DocumentType.InspectionReportPhotos:
        return "Inspection Report Photos";
      case DocumentType.ImportDeclaration:
        return "Import Declaration";
      case DocumentType.ManagementSystemDocuments:
        return "Management System Documents";
      case DocumentType.OtherQualityrelatedDocuments:
        return "Other Quality Related Documents";
      case DocumentType.ManufacturersProductSpecifications:
        return "Manufacturers Product Specifications";
      case DocumentType.ManufacturerDeclaration:
        return "Manufacturer Declaration";
      case DocumentType.CompetitorProductCertificatesRegistrationsOrLicences:
        return "Competitor Product Certificates Registrations or Licences";
      case DocumentType.NominationRiskAssessmentForm:
        return "Nomination Risk AssessmentForm";
      case DocumentType.QualityCertificate:
        return "Quality Certificate";
      case DocumentType.ManualCertificate:
        return "Manual Certificate";
      case DocumentType.HomologationCertificate:
        return "Homologation Certificate";
      case DocumentType.FactoryAuditReport:
        return "Factory Audit Report";
      case DocumentType.DesignDrawing:
        return "Design Drawing";
      case DocumentType.WorkshopInspectionReport:
        return "Workshop Inspection Report";
      case DocumentType.PhotoReport:
        return "Photo Report";
    }
  }

  private async setPermissions(
    permissionService: PermissionsService,
  ): Promise<void> {
    const permissions = await permissionService.getPermissions();
    let documentPermissions = permissions.setDocumentListConfig();
    if (
      this.userRolePermissionsService.isServiceProviderCoordinator() ||
      this.userRolePermissionsService.isInspector()
    )
      documentPermissions = {
        allowAdd: true,
        allowDelete: true,
        isReadOnly: false,
      };
    this.addDocumentAllowed = documentPermissions.allowAdd;
    this.setActions(documentPermissions.allowDelete);
  }

  private setActions(deleteActionAllowed: boolean): void {
    const actions = [viewAction, emailAction, downloadAction];
    if (deleteActionAllowed) actions.push(deleteAction);
    this.actions = actions;
  }

  private async generateReport(result: any): Promise<void> {
    let loadingDialog: MatDialogRef<any> = null;
    try {
      loadingDialog = this.dialog.open(LoadingDialogComponent, {
        autoFocus: false,
        disableClose: true,
        data: "Generating Report",
      });

      if (result.reportBasedOnCode) {
        await this.inspectionReportService.generateReportBasedOnCode(
          this.inspectionId,
          result.isPdf,
          result.templateId,
          result.itemsId,
          result.emailsToSend,
        );
      } else {
        await this.inspectionReportService.generateReport(
          this.inspectionId,
          result.isPdf,
          result.templateId,
          result.itemsId,
          result.emailsToSend,
        );
      }

      this.inspectionReportService.inspectionReportGenerated.next(true);
      await this.getInspectionDocuments();
    } finally {
      loadingDialog?.close();
    }
  }

  private async getInspectionItems(): Promise<{ id: string; name: string }[]> {
    const items: ItemLight[] = await this.itemsService.getInspectionItemsLight({
      inspectionId: this.inspectionId,
    });
    return items
      .map((item) => {
        return { id: item.id, name: item.name };
      })
      .sort((a: any, b: any) => {
        if (a.name > b.name) return 1;
        else return -1;
      });
  }

  private openLoadingDialog(message: string) {
    return this.dialog.open(LoadingDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: message,
    });
  }

  public ngOnDestroy() {
    this.notifierSubscription?.unsubscribe();
    this.restartCheckedDocumentsSubscription?.unsubscribe();
  }

  public onCheckboxSelected(event) {
    this.documentService.updateDocumentsToShareByEmail(event);
  }

  public disableActionIcon(row: any, action: TableAction): boolean {
    if (this.templatesInfo?.length) {
      if (action.name === emailAction.name && row?.templateId) {
        let defaultEmails = [];
        let allowAdditionalEmails = false;
        const foundTemplate = this.templatesInfo.find(
          (template) => template.templateId == row?.templateId,
        );
        if (foundTemplate) {
          defaultEmails = foundTemplate.defaultEmails;
          allowAdditionalEmails = foundTemplate.allowAdditionalEmails;
        }
        if (!allowAdditionalEmails && !defaultEmails.length) {
          action.tooltip = "";
          return true;
        }
      }
    }
    const allowedExtensions = ["pdf", "doc", "docx", "xls", "xlsx"];
    if (action.name === viewAction.name) {
      const extension = row.name.split(".").pop()?.toLowerCase();
      if (allowedExtensions.includes(extension)) {
        return false;
      }
      action.tooltip = "";
      return true;
    }

    return false;
  }
}
